// react components
import React, {
    useEffect,
    useState,
} from 'react'
import axios from 'axios'
import {
    useDispatch,
    useSelector,
} from 'react-redux'
import {
    useHistory,
} from 'react-router'

// components
import {
    AlertSite,
    LoaderSite,
} from 'components'

// data
import {
    contentTypeDataSite,
    contentTypeModelTypeSite,
    defaultReduxState,
    reduxFormSetIsSuccessToastOpenSite,
    reduxModalErrorEventHandlerSite,
    reduxModalMainFormShowSite,
} from 'data'

// pages
import {
    Button825,
    DeleteAlert825,
    IconBlock825,
    Popover825,
} from 'pages'

// serializer
import {
    ContentTypeSettingsTypes,
} from 'serializers/site'

// services
import {
    axiosErrorHandlerSite,
    getApiUrlSiteV2,
    getAxiosHeadersSite,
} from 'services'

// props
type ActionComponent825Props = {
    conditionStyles?: any
    contentType: contentTypeModelTypeSite
    contentTypeSettings: ContentTypeSettingsTypes
    defaultFields: any | undefined
    edit: boolean
    inDetail?: boolean
    inModal?: boolean
    modalKey?: number
    object: any
    refreshData: () => void
    refreshList?: () => void
    setActivated?: React.Dispatch<boolean>
    setDeleted?: React.Dispatch<boolean>
    setShowActionPopover: React.Dispatch<boolean>
    showActionPopover: boolean
}

// main
export const ActionComponent825: React.FC<ActionComponent825Props> = React.memo(({
    conditionStyles,
    contentType,
    contentTypeSettings,
    defaultFields,
    edit,
    inDetail,
    inModal,
    modalKey,
    object,
    refreshData,
    refreshList,
    setActivated,
    setDeleted,
    setShowActionPopover,
    showActionPopover,
}) => {

    const dispatch = useDispatch()
    const history = useHistory()
    const reduxAuth = useSelector((state: defaultReduxState) => state.reduxAuth)
    const reduxText = useSelector((state: defaultReduxState) => state.reduxText.data)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)

    useEffect(() => {
        if (showActionPopover) getData(object.id)
    }, [
        object.id,
        showActionPopover,
    ])

    type actionObjectType = {
        active?: boolean
        has_delete_permission?: boolean
        has_edit_permission?: boolean
        has_website?: boolean
        name?: string
        original_image?: string
        staff_permission?: boolean
    }

    const [actionObjectState, setActionObjectState] = useState<actionObjectType>()
    const [buttons, setButtons] = useState<any[]>([])
    const [confirmationObject, setConfirmationObject] = useState<{
        functionName?: string
        header?: string
        isOpen: boolean
    }>({
        isOpen: false,
    })
    const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false)
    const [isProcessing, setIsProcessing] = useState(false)
    const [processMessage, setProcessMessage] = useState('')

    const app = contentType.split('_')[0]
    const model = contentType.split('_')[1]
    const settingsMeta = contentTypeSettings.meta

    if (!settingsMeta) return <p className='mo-error-color'>Error</p>

    function getData(objectId: string) {
        try {
            const refreshAxiosHeaders = getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch)

            if (!objectId) return
            const getUrl = getApiUrlSiteV2(`${app}/action/${model}/${objectId}/`, reduxModalSite) // Keep the last '/' for Safari !!!!!!!
            axios({
                headers: refreshAxiosHeaders,
                method: 'get',
                url: getUrl,
            })
                .then((response) => {
                    if (process.env.NODE_ENV === 'development') console.log(response)
                    if (!response.data) return
                    const actionObject: actionObjectType = response.data
                    setActionObjectState(actionObject)
                    const newButtons = []

                    // Permission
                    if (actionObject.has_edit_permission) {
                        newButtons.push(
                            {
                                handler: () => {
                                    dispatch(reduxModalMainFormShowSite({
                                        contentType,
                                        contentTypeSettings,
                                        defaultFields,
                                        objectId: object.id,
                                        refreshData,
                                        setDeleted,
                                    }))
                                    setShowActionPopover(false)
                                },
                                role: 'edit',
                                text: reduxText[10198],
                            },
                        )
                        if (typeof actionObject.active !== 'undefined') {
                            newButtons.push(
                                {
                                    handler: () => {
                                        handlePublish(actionObject.active)
                                        setShowActionPopover(false)
                                    },
                                    role: actionObject.active ? 'destructive' : '',
                                    text: actionObject.active ? reduxText[10199] : reduxText[10200],
                                },
                            )
                        }
                    }
                    if (actionObject.has_delete_permission) {
                        newButtons.push(
                            {
                                handler: () => {
                                    setIsDeleteAlertOpen(true)
                                    setShowActionPopover(false)
                                },
                                role: 'destructive',
                                text: reduxText[10201],
                            },
                        )
                    }

                    if (newButtons.length > 0) {
                        setButtons(newButtons)
                    } else {
                        setButtons([{
                            disabled: true,
                            text: reduxText[10202],
                        }])
                    }
                })
                .catch((error) => {
                    axiosErrorHandlerSite({
                        apiUrl: getUrl,
                        component: 'ActionComponent825',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'getData',
                        skipSendErrorStatusCode: [404],
                    })
                })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'ActionComponent825',
                'getData',
            ))
        }
    }

    function handleDelete() {
        try {
            const refreshAxiosHeaders = getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch)
            setProcessMessage(reduxText[10203])
            setIsProcessing(true)
            const deleteUrl = getApiUrlSiteV2(`${app}/form/${model}/update/${object.id}/`, reduxModalSite)
            axios({
                headers: refreshAxiosHeaders,
                method: 'delete',
                url: deleteUrl,
            })
                .then((response) => {
                    if (process.env.NODE_ENV === 'development') console.log(response)
                    dispatch(reduxFormSetIsSuccessToastOpenSite(true, reduxText[10204]))
                    if (setDeleted) setDeleted(true)
                    setIsProcessing(false)
                })
                .catch((error) => {
                    setIsProcessing(false)
                    axiosErrorHandlerSite({
                        apiUrl: deleteUrl,
                        component: 'ActionComponent825',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'handleDelete',
                    })
                })
        } catch (error) {
            setIsProcessing(false)
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'ActionComponent825',
                'handleDelete',
            ))
        }
    }

    function handlePublish(active?: boolean) {
        try {
            const refreshAxiosHeaders = getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch)
            const formData = new FormData()
            formData.append('active', (!active).toString())
            if (contentType === 'event_event' && object.profile?.id === 193057) {  // TODO dynamic
                if (active) {
                    formData.append('active_status', '2')
                } else {
                    formData.append('active_status', '1')
                }
            }
            const patchUrl = getApiUrlSiteV2(`${app}/form/${model}/update/${object.id}/`, reduxModalSite)
            axios({
                data: formData,
                headers: refreshAxiosHeaders,
                method: 'patch',
                url: patchUrl,
            })
                .then((response) => {
                    if (process.env.NODE_ENV === 'development') console.log(response)
                    refreshData()
                    dispatch(reduxFormSetIsSuccessToastOpenSite(true, active ? reduxText[10205] : reduxText[10206]))
                    if (setActivated) setActivated(!active)
                })
                .catch((error) => {
                    axiosErrorHandlerSite({
                        apiUrl: patchUrl,
                        component: 'ActionComponent825',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'handlePublish',
                    })
                })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'ActionComponent825',
                'handlePublish',
            ))
        }
    }

    function handleConfirmation() {
        try {
            setIsProcessing(true)
            const refreshAxiosHeaders = getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch)
            const formData: any = new FormData()
            formData.append('content_type_id', contentTypeDataSite[contentType])
            formData.append('function_name', confirmationObject.functionName)
            formData.append('object_id', object.id)
            const postUrl = getApiUrlSiteV2(`main/helper/action_function/`, reduxModalSite)
            axios({
                data: formData,
                headers: refreshAxiosHeaders,
                method: 'post',
                url: postUrl,
            })
                .then((response) => {
                    if (process.env.NODE_ENV === 'development') console.log(response)
                    // @ts-ignore
                    refreshData()
                    setIsProcessing(false)
                    if (response.data.success_url) {
                        history.push(response.data.success_url)
                    }
                })
                .catch((error) => {
                    setIsProcessing(false)
                    axiosErrorHandlerSite({
                        apiUrl: postUrl,
                        component: 'ActionComponent825',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'handleButtonClick',
                    })
                })
        } catch (error) {
            setIsProcessing(false)
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'ActionComponent825',
                'handleButtonClick',
            ))
        }
    }

    return (
        <React.Fragment>
            <Popover825
                buttons={buttons}
                color2={!inDetail && conditionStyles?.color}
                edit={edit}
                extraZIndex={inModal}
                isOpen={showActionPopover}
                setIsOpen={setShowActionPopover}
            />
            {!settingsMeta.disableFormEdit && (
                inDetail ? (
                    <Button825
                        edit={false}
                        onClick={() =>
                            dispatch(reduxModalMainFormShowSite({
                                contentType,
                                contentTypeSettings,
                                defaultFields,
                                objectId: object.id,
                                profileObject: object.profile,
                                refreshData,
                                setDeleted,
                            }))
                        }
                        padding='0 10px 0 10px'
                        size='x-small'
                        text={reduxText[10198]}
                    />
                ) : (
                    <IconBlock825
                        color2={!inDetail && conditionStyles?.color}
                        edit={false}
                        iconClass='mo-new-icon-pencil-alt-solid'
                        onClick={() =>
                            dispatch(reduxModalMainFormShowSite({
                                contentType,
                                contentTypeSettings,
                                defaultFields,
                                objectId: object.id,
                                profileObject: object.profile,
                                refreshData,
                                setDeleted,
                            }))
                        }
                    />
                )
            )}
            {confirmationObject.isOpen && (
                <AlertSite
                    buttons={[
                        {
                            handler: () => setShowActionPopover(false),
                            text: reduxText[10207],
                        },
                        {
                            handler: () => handleConfirmation(),
                            text: reduxText[8409],
                        },
                    ]}
                    header={object.name}
                    message={confirmationObject.header}
                    isOpen
                    onDidDismiss={() => setConfirmationObject({ isOpen: false })}
                />
            )}
            {isDeleteAlertOpen && (
                <DeleteAlert825
                    handleDelete={handleDelete}
                    objectName={actionObjectState?.name}
                    setIsDeleteAlertOpen={setIsDeleteAlertOpen}
                />
            )}
            {isProcessing && (
                <LoaderSite
                    isOpen
                    message={processMessage}
                />
            )}
        </React.Fragment>
    )
})
